import { useCallback, useState } from 'react';

import { Shell } from '../../componentes/Shell.jsx';
import { obtenerFechaDeNegocioHoy } from '../../nucleo/fecha/fechaLocal.js';
import { useConsultaDeListado } from '../../nucleo/listados/useConsultaDeListado.js';
import { MenuDeTiposDeMovimiento } from '../../nucleo/navegacion/MenuDeTiposDeMovimiento.jsx';
import { consultarCuentas } from '../cuentas/api/cuentasApi.js';
import { consultarMovimientos } from '../movimientos/api/movimientosApi.js';
import { consultarTablero as consultarPresupuestoDelMes } from '../presupuesto/api/presupuestoApi.js';
import { BloqueDeVencimientosDelTablero } from '../vencimientos/componentes/BloqueDeVencimientosDelTablero.jsx';

import '../../estilos/tablero.css';
import { CabeceraDeTresCifras } from './componentes/CabeceraDeTresCifras.jsx';
import { TarjetaDelPresupuesto } from './componentes/TarjetaDelPresupuesto.jsx';
import { TarjetaDelResumenDelMes } from './componentes/TarjetaDelResumenDelMes.jsx';
import { TarjetaDeUltimosMovimientos } from './componentes/TarjetaDeUltimosMovimientos.jsx';
import {
  RENGLONES_DE_ULTIMOS_MOVIMIENTOS, nombreDelMes as nombreDeMes, periodoDeFechaLocal,
  subtituloDePeriodo,
} from './logica/agregacion.js';

/**
 * EL TABLERO · LA CASA DE LA APLICACIÓN (RF-208 a RF-214, D-309).
 *
 * ====================================================================
 * COMPONE LOS CONTRATOS YA APROBADOS; NO TIENE ENDPOINT PROPIO (D-313).
 * ====================================================================
 * Cada cifra sale de la MISMA operación contratada que alimenta su pantalla de
 * origen. Un `GET /api/tablero` que agregara todo sería una SEGUNDA vía de cálculo
 * para las mismas cifras, y toda segunda vía diverge tarde o temprano: este
 * proyecto ya nombró esa divergencia como defecto (RN-278, generalizada en
 * RN-600). Son cuatro lecturas al abrir, cero backend nuevo.
 *
 * LAS CUATRO FUENTES, UNA POR TARJETA:
 *   · ConsultarMovimientos (3/6) → resumen del mes, últimos movimientos y la
 *     cifra «Gasto de <mes>». UNA SOLA LLAMADA sirve a los tres (RN-601, RN-609).
 *   · ConsultarPresupuestoDelMes (5/6) → tarjeta de presupuesto y «Disponible».
 *   · ConsultarVencimientos (6/6) → el bloque de próximos vencimientos, que se
 *     MUEVE aquí tal cual y no se rehace (RF-213, RN-608).
 *   · ConsultarCuentas (2/6) → «Saldo de tus cuentas», por moneda (RN-611).
 *
 * RNF-112 · SE DISPARAN EN PARALELO al montar —cada `useConsultaDeListado` tiene su
 * propio efecto— y CADA TARJETA tiene su estado de carga y su error con
 * «Reintentar». Una fuente caída apaga su tarjeta, nunca el Tablero entero ni la
 * navegación, y jamás se muestra una cifra vieja como si fuera actual.
 *
 * RNF-116 · EL TABLERO NO ESCRIBE. Todas sus operaciones son de solo lectura;
 * capturar, pagar o definir presupuesto se hacen en sus pantallas.
 *
 * RN-599 · SIEMPRE EL MES EN CURSO y sin selector de mes: para mirar otros meses
 * están Movimientos y Presupuesto. La fecha es la LOCAL del dispositivo y viaja
 * como `fechaLocal`; el servidor nunca la suple con su reloj (D-06).
 *
 * RF-152 · NO LLEVA FLECHA DE VOLVER: es la casa, no hay pantalla padre a la que
 * regresar. Por eso no se le pasa `volverA` al armazón.
 *
 * S-14-05 · ORDEN VERTICAL APROBADO, que no se reordena: cabecera de tres cifras,
 * resumen del mes, presupuesto, próximos vencimientos y últimos movimientos.
 */
export default function Tablero() {
  const hoy = obtenerFechaDeNegocioHoy();
  const idPeriodo = periodoDeFechaLocal(hoy);
  const nombreDelMes = nombreDeMes(hoy);

  const [menuDeCapturaAbierto, setMenuDeCapturaAbierto] = useState(false);

  /* --- Fuente 1 · Movimientos del mes (3/6) -----------------------------
     Valores predeterminados del contrato: `incluirCancelados=false` y sin
     `renglonesRepartidos`, para heredar sin esfuerzo las reglas ya aprobadas.
     `tamanoPagina=3` es D-316: pedir una página corta es uso normal del
     contrato, no un cambio. Sin filtro de tipo: entran todos. */
  const consultarDelMes = useCallback(
    () => consultarMovimientos({
      idPeriodo,
      fechaLocal: hoy,
      pagina: 1,
      tamanoPagina: RENGLONES_DE_ULTIMOS_MOVIMIENTOS,
    }),
    [idPeriodo, hoy],
  );

  /* `estaVacio` se deja en falso a propósito en las tres consultas: el «vacío» de
     cada tarjeta tiene su propia regla —RN-604, RN-607, RN-610— y la decide la
     lógica del Tablero, no el estado genérico del listado. Lo que sí se aprovecha
     del núcleo son CARGANDO y ERROR con su «Reintentar» (D-83, D-86). */
  const movimientos = useConsultaDeListado(consultarDelMes, () => false);

  /* --- Fuente 2 · Presupuesto del mes (5/6) ---------------------------- */
  const consultarPresupuesto = useCallback(
    () => consultarPresupuestoDelMes({ idPeriodo, fechaLocal: hoy }),
    [idPeriodo, hoy],
  );
  const presupuesto = useConsultaDeListado(consultarPresupuesto, () => false);

  /* --- Fuente 3 · Cuentas activas (2/6) -------------------------------- */
  const consultarCuentasActivas = useCallback(
    () => consultarCuentas({ soloActivas: true }),
    [],
  );
  const cuentas = useConsultaDeListado(consultarCuentasActivas, () => false);

  /* --- Fuente 4 · Vencimientos (6/6): la consulta vive DENTRO del bloque,
         que se muda tal cual y conserva su conducta (RF-213, RN-608). ----- */

  return (
    <Shell titulo="Tablero" subtitulo={subtituloDePeriodo(hoy)}>
      <CabeceraDeTresCifras
        nombreDelMes={nombreDelMes}
        cuentas={cuentas}
        movimientos={movimientos}
        presupuesto={presupuesto}
      />

      <TarjetaDelResumenDelMes
        nombreDelMes={nombreDelMes}
        movimientos={movimientos}
        alCapturar={() => setMenuDeCapturaAbierto(true)}
      />

      <TarjetaDelPresupuesto nombreDelMes={nombreDelMes} presupuesto={presupuesto} />

      {/* T-3 · D-309.b · EL BLOQUE SE MUEVE AL TABLERO, intacto. Deja de depender
          del andamiaje del 1/6 y conserva sus tres reglas duras: si ninguna
          tarjeta avisa NO se dibuja (RN-277), muestra exactamente las mismas
          cifras que la pestaña Vencimientos (RN-278) y el corte en curso jamás
          aparece (RN-283). Con `base="/vencimientos"` sus enlaces llegan al panel
          y al detalle desde esta nueva sede. */}
      <BloqueDeVencimientosDelTablero base="/vencimientos" />

      <TarjetaDeUltimosMovimientos nombreDelMes={nombreDelMes} movimientos={movimientos} />

      {/* El acceso a capturar del mes vacío abre el MISMO menú de tipos del
          armazón (RF-150, D-244): una sola puerta y un solo catálogo. */}
      <MenuDeTiposDeMovimiento
        abierto={menuDeCapturaAbierto}
        alCerrar={() => setMenuDeCapturaAbierto(false)}
      />
    </Shell>
  );
}
